import React, { useState, useRef, useEffect, forwardRef } from "react";
import { useIntl } from "react-intl";
import { add, debounce } from "lodash";
import { useJsApiLoader } from "@react-google-maps/api";
import PlacesAutocomplete from "react-places-autocomplete";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import Field from "../../atoms/field/Field.jsx";

const libraries = ["places"];

const InputAutocomplete = ({
  updateCoordinates,
  updateStreet,
  errors,
  onSaveClick,
  name,
  data,
  required,
}) => {
  const intl = useIntl();
  const inputRef = useRef(null);
  const [address, setAddress] = useState(
    data.delivery_address_street
      ? `${data.delivery_address_street}, ${data.delivery_address_postcode_number}, ` +
          `${data.delivery_address_postcode_city}`
      : null,
  );
  const [showDropdown, setShowDropdown] = useState(true);
  const usr_postcode = data.delivery_address_postcode_number.replace(
    /[^\d]/g,
    "",
  );
  const [error, setError] = useState(null);
  const errorMessagePostcode = intl.formatMessage(
    { id: "postcodeError" },
    { postcode: usr_postcode },
  );
  const errorMessageMissingStreetNumber = intl.formatMessage({
    id: "missingStreetNumber",
  });
  const errorNoResultFromPlacesAPI = intl.formatMessage({
    id: "noResultFromPlacesAPI",
  });
  const errorFetchingCordinates = intl.formatMessage({
    id: "noCordinatesFoundForAddress",
  });

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: window.PROPS.GOOGLE_PLACES_API_KEY_FRONTEND,
    libraries,
    language: intl.locale,
  });

  const onStreetNumberFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      const regex = /^[^,]+/;
      const match = address?.match(regex);
      if (match) {
        const endIndex = match[0].length;
        inputRef.current.setSelectionRange(endIndex, endIndex);
      }
    }
  };

  const searchOptions = {
    componentRestrictions: {
      country: usr_postcode.length === 5 ? "de" : "at",
    },
    types: ["address"],
  };

  const apiOnError = (status) => {
    if (status === "ZERO_RESULTS") setError(errorNoResultFromPlacesAPI);
    else setError(status);
  };

  const validate = (
    postcode,
    usr_postcode,
    formattedAddress,
    addressCoordinates,
    streetNumber,
  ) => {
    if (usr_postcode !== postcode) {
      return {
        error: errorMessagePostcode,
        address: formattedAddress,
        streetStatus: "error",
      };
    } else if (!streetNumber) {
      return {
        error: errorMessageMissingStreetNumber,
        address: formattedAddress,
        streetStatus: "error",
      };
    } else if (!addressCoordinates?.lat) {
      return {
        error: errorFetchingCordinates,
        address: formattedAddress,
        streetStatus: "error",
      };
    } else {
      return { error: null };
    }
  };

  const getAddressDetails = (results) => {
    const getAddressComponent = (type) =>
      results[0]?.address_components.find((component) =>
        component.types.includes(type),
      )?.long_name;

    const number = getAddressComponent("street_number");
    const street = getAddressComponent("route");
    const postcode = getAddressComponent("postal_code");

    return { number, street, postcode };
  };

  useEffect(() => {
    isLoaded && onStreetNumberFocus();
  }, [address, isLoaded]);

  const handleSelect = async (value) => {
    updateStreet(undefined);
    try {
      const results = await geocodeByAddress(value);
      const addressCoordinates = await getLatLng(results[0]);
      const formattedAddress = results[0]?.formatted_address;
      const { number, street, postcode } = getAddressDetails(results);
      const validationResult = validate(
        postcode,
        usr_postcode,
        formattedAddress,
        addressCoordinates,
        number,
      );
      if (validationResult.error) {
        setError(validationResult.error);
        setAddress(validationResult.address);
        updateStreet(validationResult.streetStatus);
        return { success: false, street: null };
      }
      setAddress(formattedAddress);
      updateCoordinates(`${addressCoordinates.lat}, ${addressCoordinates.lng}`);
      updateStreet(`${street} ${number}`);
      setError(null);
      return { success: true, street: `${street} ${number}` };
    } catch (error) {
      setError(error.message);
      return { success: false, street: null };
    }
  };

  const handleChange = (newValue) => {
    setAddress(newValue);
    setError(null);
    setShowDropdown(true);
  };

  useEffect(() => {
    if (address && isLoaded) {
      handleSelect(address);
    }
  }, [isLoaded]);

  const debouncedHandleSelect = debounce(handleSelect, 800);

  if (loadError)
    return <div>Error loading Google Maps API: {loadError.message}</div>;
  if (!isLoaded) return <div>Loading...</div>;
  const errorToRender = error ?? errors.message;
  return (
    <div>
      <PlacesAutocomplete
        onChange={handleChange}
        onSelect={debouncedHandleSelect}
        value={address}
        searchOptions={searchOptions}
        onError={apiOnError}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div>
            <Field
              {...getInputProps({
                placeholder: intl.formatMessage({
                  id: "streetAndHouseNumberAutocomplete",
                }),
                className: "location-search-input",
                required: required,
                onBlur: async (e) => {
                  const result = await handleSelect(e.target.value);
                  if (
                    e?.relatedTarget?.id == "save-button-form" &&
                    result.success
                  ) {
                    await onSaveClick(result.street);
                  }
                },
              })}
              name={name}
              inputRef={inputRef}
              errors={errors}
            />
            {showDropdown && (
              <div className="autocomplete-dropdown-container">
                {loading && <div>Loading...</div>}
                {suggestions.map((suggestion) => {
                  const className = suggestion.active
                    ? "suggestion-item--active"
                    : "suggestion-item";
                  return (
                    <div
                      key={suggestion.index}
                      {...getSuggestionItemProps(suggestion, {
                        className,
                        onClick: () => setShowDropdown(false),
                      })}
                    >
                      <span className="span-suggestion">
                        {suggestion.description}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </PlacesAutocomplete>
      {errorToRender && (
        <div className="field__info field__error">{errorToRender}</div>
      )}
    </div>
  );
};

export default InputAutocomplete;
